import { ShopifyOrderChanges } from "../components/order-conflicts/ShopifyOrderChanges";
import { StockSchedulePanel } from "../components/stock-schedule/StockSchedulePanel";
import { CustomerImportPanel } from "../components/customer-import/CustomerImportPanel";
import { ResourceEventsPanel } from "../components/resource-events/ResourceEventsPanel";
import { OrderEventsPanel } from "../components/order-events/OrderEventsPanel";
import { OrderImportPanel } from "../components/order-import/OrderImportPanel";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Select,
  Page,
  Text,
} from "@shopify/polaris";
import { useAuth } from "../App";
import { importRequest } from "../../../packages/shared/product-import/client";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../packages/shared/shopify-queue/client";
import {
  ImportControls,
  type ImportData,
} from "../components/product-import/ImportControls";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
export default function SyncSettings() {
  const [section, setSection] = useState<
    "products" | "orders" | "customers" | "events" | "resources" | "stock"
  >("products");
  const { user, currentShop, permissions } = useAuth(),
    allow = (permission: string) =>
      permissions.includes("*") || permissions.includes(permission);
  const account = useMemo(
    () => ({
      userId: String(user?.id || ""),
      domain: currentShop?.domain || "",
      organizationId: currentShop?.organizationId,
    }),
    [user?.id, currentShop?.domain, currentShop?.organizationId],
  );
  return (
    <Page title="Sync Settings">
      <BlockStack gap="400">
        <InlineStack gap="200">
          <Button
            pressed={section === "products"}
            onClick={() => setSection("products")}
          >
            Products
          </Button>
          <Button
            pressed={section === "orders"}
            onClick={() => setSection("orders")}
          >
            Orders
          </Button>
          <Button
            pressed={section === "events"}
            onClick={() => setSection("events")}
          >
            Order updates
          </Button>
          <Button
            pressed={section === "stock"}
            onClick={() => setSection("stock")}
          >
            Stock schedule
          </Button>
          <Button
            pressed={section === "customers"}
            onClick={() => setSection("customers")}
          >
            Customers
          </Button>
          <Button
            pressed={section === "resources"}
            onClick={() => setSection("resources")}
          >
            Catalogue and customers
          </Button>
        </InlineStack>
        {(section === "orders" || section === "events") && allow("sync.orders") && <ShopifyOrderChanges />}
        {!allow("sync.status.view") ? (
          <Banner tone="critical">
            You do not have access to sync settings.
          </Banner>
        ) : !account.userId || !account.domain ? (
          <Banner>Select a company first.</Banner>
        ) : section === "stock" ? (
          <StockSchedulePanel
            key={queueAccountKey(account)}
            account={account}
            canSchedule={
              allow("inventory.sync") && allow("sync.schedules.manage")
            }
          />
        ) : section === "customers" ? (
          <CustomerImportPanel
            key={queueAccountKey(account)}
            account={account}
            canImport={allow("settings.manage")}
            canSchedule={
              allow("settings.manage") && allow("sync.schedules.manage")
            }
          />
        ) : section === "resources" ? (
          <ResourceEventsPanel
            key={queueAccountKey(account)}
            account={account}
            canProducts={allow("sync.products")}
            canCustomers={allow("settings.manage")}
          />
        ) : section === "events" ? (
          <OrderEventsPanel
            key={queueAccountKey(account)}
            account={account}
            canRetry={allow("sync.orders")}
          />
        ) : section === "orders" ? (
          <OrderImportPanel
            key={queueAccountKey(account)}
            account={account}
            canImport={allow("sync.orders")}
            canSchedule={allow("sync.orders") && allow("sync.schedules.manage")}
          />
        ) : (
          <CompanyImports
            key={queueAccountKey(account)}
            account={account}
            canImport={allow("sync.products")}
            canSchedule={
              allow("sync.products") && allow("sync.schedules.manage")
            }
          />
        )}
      </BlockStack>
    </Page>
  );
}
export function CompanyImports({
  account,
  canImport,
  canSchedule,
}: {
  account: QueueAccount;
  canImport: boolean;
  canSchedule: boolean;
}) {
  const [data, setData] = useState<ImportData | null>(null),
    [error, setError] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [refresh, setRefresh] = useState(0),
    [connectionId, setConnectionId] = useState(""),
    cursor = pages[pages.length - 1];
  const reload = useCallback(() => {
    setPages([]);
    setRefresh((v) => v + 1);
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void importRequest<ImportData>(
      base,
      account,
      "?" + new URLSearchParams({ ...connectionId ? { connection_id: connectionId } : {}, ...cursor ? { cursor: String(cursor) } : {} }),
      { signal: abort.signal },
    )
      .then((v) => {
        if (!abort.signal.aborted) setData(v);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, cursor, refresh, connectionId]);
  return (
    <BlockStack gap="400">
      <InlineStack gap="200">
        <Button onClick={reload}>Refresh imports</Button>
        <Button url="/shopify-updates">
          Publish stock and view Shopify updates
        </Button>
      </InlineStack>
      {error && <Banner tone="critical">{error}</Banner>}
      {!data && !error && <Text as="p">Loading product imports…</Text>}
      {data && <Select label="Import store" value={data.selected_connection_id || ""}
        options={[{label: "Choose a store", value: ""}, ...data.stores.map(store => ({label: store.domain + (store.connected ? "" : " (disconnected)"), value: store.id}))]}
        onChange={id => {setData(null); setConnectionId(id); reload();}} />}
      {data && !data.selected_connection_id && data.stores.length > 1 && <Banner>Choose a store to view its imports and schedule.</Banner>}
      {data && (data.selected_connection_id || !data.stores.length) && (
        <ImportControls
          key={data.selected_connection_id || "none"}
          data={data}
          account={account}
          canImport={canImport}
          canSchedule={canSchedule}
          onSaved={reload}
        />
      )}
      <InlineStack gap="200">
        {!!pages.length && (
          <Button onClick={() => setPages((p) => p.slice(0, -1))}>
            Newer imports
          </Button>
        )}
        {data?.nextCursor && (
          <Button onClick={() => setPages((p) => [...p, data.nextCursor!])}>
            Older imports
          </Button>
        )}
      </InlineStack>
      {!!data?.legacySchedules.length && (
        <Banner tone="warning">
          Older schedule settings are retained for review. They had no working
          runner and have not been enabled here.
        </Banner>
      )}
      <Card>
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">
            Other Shopify updates
          </Text>
          <Text as="p">
            Stock publication has its own reviewed queue. Order imports have
            their own tab. Store event messages are separate from these
            schedules; this page does not change their subscriptions.
          </Text>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
